import { useCallback, useEffect, useState } from 'react';
import { SPOTIFY_CANVAS_ERROR_CODES, type SpotifyCanvasSettings } from 'fym-shared/src/music24/spotifyCanvas';
import { useUpdateSpotifyCanvasSettingsMutation } from '../../../../redux/api/fymAPI';
import { isSuccessfulResult } from '../../../../redux/api/utils/isSuccessfulResult';
import { canvasErrorCode } from './spotifyCanvasErrors';

/**
 * The switch "Upload Canvas to Spotify automatically" (design spotify-canvas b1, b2, b5): one per
 * artist. It moves at once and shows the spinner knob until the PUT answers; a refused PUT moves
 * it back with "That did not work. Try again.". To turn it on with no accepted terms it opens the
 * terms dialog first (b3); "Accept and turn on" sends `acceptTerms: true`. Off needs no dialog.
 */
export function useSpotifyCanvasSwitch({
  accountId,
  settings,
  readOnly,
}: {
  accountId: number;
  settings: SpotifyCanvasSettings | undefined;
  readOnly: boolean;
}) {
  const [update, { isLoading }] = useUpdateSpotifyCanvasSettingsMutation();
  /** The place the switch moved to, until the settings read shows it. */
  const [pending, setPending] = useState<boolean | null>(null);
  const [failed, setFailed] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [termsFailed, setTermsFailed] = useState(false);
  const saved = settings?.autoUpload ?? false;

  useEffect(() => {
    if (pending !== null && !isLoading && pending === saved) {
      setPending(null);
    }
  }, [isLoading, pending, saved]);

  const save = useCallback(
    async (autoUpload: boolean, acceptTerms: boolean): Promise<void> => {
      setFailed(false);
      setTermsFailed(false);
      setPending(autoUpload);
      const result = await update({
        accountId,
        body: acceptTerms ? { autoUpload, acceptTerms: true } : { autoUpload },
      });
      if (isSuccessfulResult(result)) {
        setTermsOpen(false);
        return;
      }
      setPending(null);
      if (canvasErrorCode(result.error) === SPOTIFY_CANVAS_ERROR_CODES.termsRequired) {
        setTermsOpen(true);
        return;
      }
      if (acceptTerms) {
        setTermsFailed(true);
      } else {
        setFailed(true);
      }
    },
    [accountId, update]
  );

  const isOn = pending ?? saved;
  const toggle = useCallback(() => {
    if (readOnly || isLoading || settings === undefined) {
      return;
    }
    if (!isOn && settings.termsAcceptedAt === null) {
      setFailed(false);
      setTermsFailed(false);
      setTermsOpen(true);
      return;
    }
    save(!isOn, false).catch(console.error);
  }, [isLoading, isOn, readOnly, save, settings]);

  return {
    isOn,
    isSaving: isLoading,
    failed,
    toggle,
    termsOpen,
    termsFailed,
    acceptAndTurnOn: useCallback(() => {
      save(true, true).catch(console.error);
    }, [save]),
    closeTerms: useCallback(() => setTermsOpen(false), []),
  };
}
